import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import { activateDocumentEscapeGuard, deactivateDocumentEscapeGuard } from '../utils/mylexDocumentEscapeGuard';

// Form values to post
type Values = {
    documentId: string,
    firstname: string,
    lastname: string,
    email: string,
    documentTitle: string,
    viewId: string
}

// Result after post
type DocumentRequestResponse = {
    formPostResponseMessage: string,
    ok: boolean,
    messageClass: string,
}

/** 
 *  Model dialog that will be displayed when the user clicks a data-document-request link.
 **/
const DocumentRequestModal = () => {
    const [show, setShow] = useState(false);
    const [validated, setValidated] = useState(false);
    const [formAction, setFormAction] = useState("");
    const [isFormPostDisabled, setFormPostDisabled] = useState(false);
    const [isBulkdownloadRequest, setBulkDownload] = useState(false);

    const documentIdQueryStringKey = "document-request-id";
    const bulkDownloadQueryStringKey = "document-request-bulkdownload";
    const documentTitleQueryStringKey = "document-request-title";
    const viewIdQueryStringKey = "document-request-viewid";

    /**
     * Resets the state of the controll back to the initial values.
     * */
    const resetState = () => {
        setValues({
            documentId: "",
            firstname: "",
            lastname: "",
            email: "",
            documentTitle: "",
            viewId: ""
        });

        setDocumentRequestResponse({
            formPostResponseMessage: "",
            messageClass: "",
            ok: false
        });

        setValidated(false);
        setFormAction("");
        setFormPostDisabled(false);
    }

    const handleClose = () => {
        setShow(false);
        resetState();
    }

    const [values, setValues] = useState<Values>({
        documentId: "",
        firstname: "",
        lastname: "",
        email: "",
        documentTitle: "",
        viewId: ""
    });

    const [documentRequestResponse, setDocumentRequestResponse] = useState<DocumentRequestResponse>({
        formPostResponseMessage: "",
        messageClass: "",
        ok: false
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValues({ ...values, [event.target.name]: event.target.value });
    }

    const formRef = React.useRef<HTMLFormElement>(null);
    const triggerRef = React.useRef<HTMLElement | null>(null);

    const firstnameRef = React.useRef<HTMLInputElement>(null);
    const lastnameRef = React.useRef<HTMLInputElement>(null);
    const emailRef = React.useRef<HTMLInputElement>(null);

    const errId = (name: string) => `requestForm.${name}.error`;

    const isInvalidEl = (el: HTMLInputElement | null) =>
        Boolean(validated && el && !el.checkValidity());

    /**
     * Click event handler for the document.
     * When the user clicks on an document request link, the modal dialog will shown.
     * 
     * Example link: <a href="http://mlt-mlp4.mlt.local/web/requestdocument/PostDocument/?document-request-id=123456789">Request document deeplink</a>
     * 
     * @param event the document click event
     */
    const documentClickLinkHandler = (event: MouseEvent) => {

        const anchor = (event.target as HTMLElement).closest('a');
        if (!anchor || !anchor.hasAttribute('href')) {
            return;
        }

        // Check if the url does not start with #
        if (anchor.getAttribute('href')?.startsWith("#"))
            return;

        try {
            // Check if the href contains a query string param "document-request-id" 
            const url = new URL(anchor.getAttribute('href') ?? "");

            if (url.searchParams.get(documentIdQueryStringKey) == null)
                return;

            // We are dealing with a document request link, stop default behaviour, and deal with the click.
            event.preventDefault();
            event.stopPropagation();

            // Get the required data properties from the link.
            setValues({
                ...values,
                documentId: url.searchParams.get(documentIdQueryStringKey) ?? "",
                documentTitle: url.searchParams.get(documentTitleQueryStringKey) ?? "",
                viewId: url.searchParams.get(viewIdQueryStringKey) ?? ""             
            });

            const _bulkDownloadQueryParam = (url.searchParams.get(bulkDownloadQueryStringKey) ?? "") === "true";
            setBulkDownload(_bulkDownloadQueryParam);

            // Set the form action to the url in the href, but withouth the query string param.
            setFormAction(url.href.replace(url.search, ""));

            triggerRef.current = anchor;

            activateDocumentEscapeGuard();
            setShow(true);

        } catch (error) {
            console.log(error);
            return;
        }
    }

    // Register the document event listener in the React.useEffect so the event are added after the page is loaded.
    React.useEffect(() => {
        document.addEventListener("click", documentClickLinkHandler, false);

        return () => {
            document.removeEventListener("click", documentClickLinkHandler);
            deactivateDocumentEscapeGuard();
            restoreFocusToTrigger();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []);

    const handleModalExited = () => {
        deactivateDocumentEscapeGuard();
        restoreFocusToTrigger();
    };

    const restoreFocusToTrigger = () => {
        const trigger = triggerRef.current;
        triggerRef.current = null;

        if (trigger && typeof trigger.focus === 'function') {
            trigger.focus();
        }
    };

    /**
     * Create the document request and sends it to the configured formAction.
     */
    const sendDocumentRequest = () => {
        fetch(formAction,
            {
                method: "POST",
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(values)
            }
        ).then((res) => {
            //console.log(res);
            if (res.ok) {
                setFormPostDisabled(true);
                setDocumentRequestResponse({
                    formPostResponseMessage: "",
                    messageClass: "text-success",
                    ok: res.ok
                });
            } else {
                throw Error(res.statusText);
            }
        }).catch((error) => {
            //console.log(error);
            setDocumentRequestResponse({
                formPostResponseMessage: "Document aanvraag is niet gelukt vanwege een interne fout.\r\nAls deze fout zich blijft herhalen, neem dan contact op met uw applicatiebeheerder voor hulp",
                messageClass: "text-danger mb-3",
                ok: false
            });
        });
    }

    /**
     * The form submit handler trigger. 
     * Will validate the form and sends the document request to the configured destination.
     * 
     * @param event the button click event.
     */
    const submitFormHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        console.log(JSON.stringify(values));

        // Validation failed
        event.preventDefault();
        event.stopPropagation();

        setValidated(true);

        const form = formRef.current;
        if (!form) return;

        if (!form.checkValidity()) {
            // Focus first invalid in your desired order:
            if (firstnameRef.current && !firstnameRef.current.checkValidity()) {
                firstnameRef.current.focus();
                return;
            }
            if (lastnameRef.current && !lastnameRef.current.checkValidity()) {
                lastnameRef.current.focus();
                return;
            }
            if (emailRef.current && !emailRef.current.checkValidity()) {
                emailRef.current.focus();
                return;
            }
            return;
        }

        sendDocumentRequest();
    }

    return (
        <Modal show={show} onHide={handleClose} onExited={handleModalExited} aria-labelledby="modaltitle" aria-describedby="formhelp">
            <Modal.Header closeButton>
                <Modal.Title as="h2" id="modaltitle">
                    {isBulkdownloadRequest ? "Dossier aanvragen" : "Document aanvragen"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isBulkdownloadRequest
                    ? <div id="formhelp" className="alert alert-secondary">Om de documenten in dit dossier in te zien kun je de onderstaande gegevens invullen.<br /><br />Nadat je op de knop 'Dossier aanvragen' hebt gedrukt krijg je via e-mail direct een link toegestuurd om in een keer alle de documenten in het dossier te downloaden.</div>
                    : <div id="formhelp" className="alert alert-secondary">Om dit document in te zien kun je de onderstaande gegevens invullen.<br /><br />Nadat je op de knop 'Document aanvragen' hebt gedrukt krijg je via e-mail direct een link toegestuurd om het document te downloaden.</div>
                }
                <Form noValidate validated={validated} ref={formRef}>
                    <Form.Control type="hidden" value={values.documentId} name="documentId" />

                    <Form.Group className="mb-3" controlId="requestForm.Firstname">
                        <Form.Label className="h5">Voornaam</Form.Label>
                        <Form.Control
                            ref={firstnameRef}
                            required
                            type="text"
                            onChange={handleChange}
                            value={values.firstname}
                            name="firstname"
                            autoComplete="given-name"
                            autoFocus
                            isInvalid={isInvalidEl(firstnameRef.current)}
                            aria-invalid={isInvalidEl(firstnameRef.current) ? "true" : undefined}
                            aria-describedby={isInvalidEl(firstnameRef.current) ? errId("firstname") : undefined}
                        />
                        <Form.Control.Feedback id={errId("firstname")} type="invalid">
                            Veld is verplicht.
                        </Form.Control.Feedback>
                        
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="requestForm.Lastname">
                        <Form.Label className="h5">Achternaam</Form.Label>
                        <Form.Control
                            ref={lastnameRef}
                            required
                            type="text"
                            onChange={handleChange}
                            value={values.lastname}
                            name="lastname"
                            autoComplete="family-name"
                            isInvalid={isInvalidEl(lastnameRef.current)}
                            aria-invalid={isInvalidEl(lastnameRef.current) ? "true" : undefined}
                            aria-describedby={isInvalidEl(lastnameRef.current) ? errId("lastname") : undefined}
                        />
                        <Form.Control.Feedback id={errId("lastname")} type="invalid">
                            Veld is verplicht.
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="requestForm.Email">
                        <Form.Label className="h5">E-mailadres</Form.Label>
                        <Form.Control
                            ref={emailRef}
                            required
                            type="email"
                            onChange={handleChange}
                            value={values.email}
                            name="email"
                            autoComplete="email"
                            isInvalid={isInvalidEl(emailRef.current)}
                            aria-invalid={isInvalidEl(emailRef.current) ? "true" : undefined}
                            aria-describedby={isInvalidEl(emailRef.current) ? errId("email") : undefined}
                        />
                        <Form.Control.Feedback id={errId("email")} type="invalid">
                            Veld is verplicht en dient een geldig e-mailadres te zijn.
                        </Form.Control.Feedback>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {documentRequestResponse.formPostResponseMessage && (
                    <div className={`${documentRequestResponse.messageClass} whitespace-pre-line`} role="alert">
                        {documentRequestResponse.formPostResponseMessage}
                    </div>
                )}
               
                {!documentRequestResponse.ok &&
                    <div>
                        <Button variant="secondary" onClick={handleClose} className="me-2 focus-ring">
                            Annuleren
                        </Button>

                        <Button variant="primary" onClick={submitFormHandler} disabled={isFormPostDisabled}>
                            { isBulkdownloadRequest ? "Dossier aanvragen" : "Document aanvragen" }      
                        </Button>
                    </div>
                }

                {documentRequestResponse.ok &&
                    <div>
                        <Button variant="secondary" onClick={handleClose} className="focus-ring">
                            <i className="text-success icon-check-circle-fill"></i> Sluiten
                        </Button>
                    </div>
                }

            </Modal.Footer>

        </Modal>
    );
}

export default DocumentRequestModal;